type TagKey = keyof HTMLElementTagNameMap;

function escapeHtml(unsafe: string) {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function parseTagIdAndClasses<T extends TagKey>(
    input: string,
): {
    tag: T;
    id?: string;
    classes: string[];
} {
    // Tag part is up to first '.' or '#'
    const tagStr = input.split(/[.#]/)[0];
    const tag = tagStr as T;

    const afterTag = input.slice(tagStr.length);

    const idMatch = afterTag.match(/#([^#.]+)/);
    const id = idMatch?.[1];

    const classes = [...afterTag.matchAll(/\.([^#.]+)/g)].map((m) => m[1]);

    return { tag, id, classes };
}

export function $el<T extends TagKey>(
    tagname: T,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}#${string}`,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}.${string}`,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}#${string}.${string}`,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];

export function $el<T extends TagKey>(
    tagname: T,
    properties: Partial<HTMLElementTagNameMap[T]>,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}#${string}`,
    properties: Partial<HTMLElementTagNameMap[T]>,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}.${string}`,
    properties: Partial<HTMLElementTagNameMap[T]>,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];
export function $el<T extends TagKey>(
    tagname: `${T}#${string}.${string}`,
    properties: Partial<HTMLElementTagNameMap[T]>,
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T];

export function $el<T extends TagKey>(
    tagname: T | string, // <-- IMPORTANT: implementation is wider
    first: Partial<HTMLElementTagNameMap[T]> | (Node | string),
    ...children: (Node | string)[]
): HTMLElementTagNameMap[T] {
    const parsed =
        typeof tagname === "string"
            && (tagname.includes("#") || tagname.includes("."))
            ? parseTagIdAndClasses<T>(tagname)
            : { tag: tagname as T, classes: [] };

    const el = document.createElement(
        parsed.tag,
    ) as unknown as HTMLElementTagNameMap[T];

    if (parsed.id) el.id = parsed.id;
    if (parsed.classes.length) el.classList.add(...parsed.classes);

    const firstIsProps =
        first != null && typeof first === "object" && !(first instanceof Node);

    if (firstIsProps) {
        const properties = first as Partial<HTMLElementTagNameMap[T]>;
        for (const p in properties) {
            const v = properties[p as keyof HTMLElementTagNameMap[T]];
            if (v !== undefined) (el as any)[p] = v;
        }
    } else {
        const c = first as Node | string;
        children = [c, ...children];
    }

    for (let c of children) {
        if (typeof c == "string") c = escapeHtml(c);
        el.append(c);
    }

    return el;
}
